import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/shared/lib/utils'

const segmentedControlVariants = cva(
  'inline-flex items-center justify-center text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-50 text-white data-[state=on]:bg-primary data-[state=on]:text-white hover:bg-primary/20',
  {
    variants: {
      size: {
        sm: 'h-8 p-1 rounded-3xl',
        default: 'h-9 px-4 py-2 rounded-3xl',
        lg: 'h-10 px-5 py-2.5 rounded-4xl',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const segmentedControlItemVariants = cva(
  'inline-flex items-center justify-center text-sm text-paper font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-50 text-white data-[state=on]:bg-primary data-[state=on]:text-white hover:bg-primary/20',
  {
    variants: {
      size: {
        sm: 'h-6 p-1 px-2 leading-5 rounded-2xl',
        default: 'h-9 px-4 py-2 rounded-3xl',
        lg: 'h-10 px-5 py-2.5 rounded-3xl',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const SegmentedControlContext = React.createContext<
  VariantProps<typeof segmentedControlVariants>
>({
  size: 'default',
})

const SegmentedControlRoot = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof segmentedControlVariants>
>(({ className, size, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn(
      'inline-flex items-center rounded-3xl p-1',
      'bg-[#111111]/50 backdrop-blur-sm',
      className
    )}
    {...props}
  >
    <SegmentedControlContext.Provider value={{ size }}>
      {children}
    </SegmentedControlContext.Provider>
  </ToggleGroupPrimitive.Root>
))

SegmentedControlRoot.displayName = ToggleGroupPrimitive.Root.displayName

const SegmentedControlItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof segmentedControlVariants>
>(({ className, children, size, ...props }, ref) => {
  const context = React.useContext(SegmentedControlContext)

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        segmentedControlItemVariants({
          size: context.size || size,
        }),
        className
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  )
})

SegmentedControlItem.displayName = ToggleGroupPrimitive.Item.displayName

const SegmentedControl = {
  Root: SegmentedControlRoot,
  Item: SegmentedControlItem,
}

export {
  SegmentedControl,
  SegmentedControlItem,
  SegmentedControlRoot,
  segmentedControlVariants,
}
